import React, {
  FormEvent,
  ForwardedRef,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styles from '@danske/sapphire-css/components/table/table.module.css';
import { useOverlayTriggerState } from 'react-stately/useOverlayTriggerState';
import { Popover } from '../../Popover';
import { IconButton } from '../../IconButton';
import {
  InteractiveTableCell,
  InteractiveTableCellProps,
} from './InteractiveTable';
import { Checkmark, EditAlt } from '@danske/sapphire-icons/react';
import { useObjectRef } from 'react-aria/useObjectRef';
import clsx from 'clsx';
import type { CellProps } from 'react-aria-components';

export interface EditableCellProps
  extends Omit<InteractiveTableCellProps, 'children'>,
    Pick<CellProps, 'children'> {
  /**
   * A function that renders the editable form content.
   * This should typically contain form inputs like TextField or Select.
   */
  renderEditor: () => ReactNode;
  /**
   * Callback fired when the user submits the edit (via Enter key or checkmark button).
   * If this returns false, the editor popover is not closed. This is useful
   * when the field contains errors.
   */
  onSubmit?: (e: FormEvent<HTMLFormElement>) => boolean | void;
  /**
   * Callback fired when the user cancels the edit (via Escape key).
   */
  onCancel?: () => void;
  /**
   * aria-label of the edit button
   * @default: 'Edit'
   */
  editButtonLabel?: string;

  /**
   * aria-label of the save button
   * @default: 'Save'
   */
  saveButtonLabel?: string;
}

/**
 * An editable cell within an InteractiveTable.
 * Displays a static value with an edit button that appears on row hover/focus.
 * When the edit button is clicked, a popover form opens allowing inline editing.
 */
export const EditableCell = forwardRef(function EditableCell(
  props: EditableCellProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const {
    children,
    renderEditor,
    onSubmit,
    onCancel,
    editButtonLabel,
    saveButtonLabel,
    ...cellProps
  } = props;

  const [isEditing, setIsEditing] = useState(false);
  const [cellPosition, setCellPosition] = useState({ width: 0, offset: 0 });
  const cellRef = useObjectRef<HTMLDivElement>(ref);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const result = onSubmit?.(e);
      if (result !== false) {
        setIsEditing(false);
      }
    },
    [onSubmit, setIsEditing]
  );

  const cancel = useCallback(() => {
    setIsEditing(false);
    onCancel?.();
  }, [setIsEditing, onCancel]);

  const popoverState = useOverlayTriggerState({
    isOpen: isEditing,
    onOpenChange: (isOpen) => {
      if (!isOpen) {
        formRef.current?.requestSubmit();
      }
    },
  });

  // Measure cell width when editing starts
  useLayoutEffect(() => {
    if (isEditing && cellRef.current) {
      const width = cellRef.current.clientWidth;
      const offset =
        -cellRef.current.offsetHeight -
        1; /* to cross over the row separator nicely */
      setCellPosition({ width, offset });
    }
  }, [isEditing]);

  useEffect(() => {
    if (isEditing) {
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          e.preventDefault();
          cancel();
        }
      };
      window.addEventListener('keydown', onKeyDown);
      return () => {
        window.removeEventListener('keydown', onKeyDown);
      };
    }
  }, [isEditing, cancel]);

  return (
    <InteractiveTableCell
      {...cellProps}
      ref={cellRef}
      wrapCellContent={(cellContent) => (
        <span
          className={clsx(styles['sapphire-table__cell-container--editable'])}
        >
          {cellContent}
          <IconButton
            aria-label={editButtonLabel ?? 'Edit'}
            variant="tertiary"
            size="sm"
            onPress={() => setIsEditing(true)}
            UNSAFE_className={clsx(styles['sapphire-table__edit-button'])}
          >
            <EditAlt />
          </IconButton>
          <Popover
            state={popoverState}
            triggerRef={cellRef}
            placement="bottom start"
            offset={cellPosition.offset}
            // We want to control the escape key manually
            isKeyboardDismissDisabled
            padded
            UNSAFE_style={{ minWidth: `${cellPosition.width}px` }}
          >
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              className={clsx(styles['sapphire-table__edit-cell-popover'])}
            >
              {renderEditor()}
              <IconButton
                size="md"
                type="submit"
                variant="tertiary"
                aria-label={saveButtonLabel ?? 'Save'}
              >
                <Checkmark />
              </IconButton>
            </form>
          </Popover>
        </span>
      )}
    >
      {children}
    </InteractiveTableCell>
  );
});
